import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import useViewerIdentity from '../hooks/useViewerIdentity';
import { getStoredAuthToken } from '../utils/authStorage';
import { subscribeToAuthEvents, AUTH_EVENT_TYPES } from '../utils/authEvents';
import { getOAuthBaseUrl } from '../services/apiClient';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead as apiMarkRead,
  markAllNotificationsRead as apiMarkAllRead,
} from '../services/notificationsApi';

const NotificationContext = createContext(null);

const SSE_RECONNECT_DELAY_MS = 3000;
const SSE_MAX_RETRIES = 5;
const SSE_BACKOFF_MULTIPLIER = 2;
const SSE_HEARTBEAT_TIMEOUT_MS = 90000; // 90s - server sends every 30s
const POLL_INTERVAL_MS = 60000;
const PAGE_SIZE = 20;

export function NotificationProvider({ children }) {
  const { user } = useViewerIdentity();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const eventSourceRef = useRef(null);
  const pollTimerRef = useRef(null);
  const sseFailedRef = useRef(false);
  const sseRetryCountRef = useRef(0);
  const sseReconnectTimerRef = useRef(null);
  const lastHeartbeatRef = useRef(Date.now());
  const heartbeatCheckerRef = useRef(null);

  // ── Unread count ──────────────────────────────
  const refreshUnreadCount = useCallback(async () => {
    try {
      const data = await fetchUnreadCount();
      setUnreadCount(data.unreadCount);
    } catch {
      // silent — will retry on next poll
    }
  }, []);

  // ── Fetch notifications (paginated) ───────────
  const loadNotifications = useCallback(async (pageNum = 0) => {
    setLoading(true);
    try {
      const data = await fetchNotifications(pageNum, PAGE_SIZE);
      const items = data.content || [];
      if (pageNum === 0) {
        setNotifications(items);
      } else {
        setNotifications((prev) => [...prev, ...items]);
      }
      setHasMore(!data.last);
      setPage(pageNum);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadNotifications(page + 1);
    }
  }, [loading, hasMore, page, loadNotifications]);

  // ── Mark as read ──────────────────────────────
  const markAsRead = useCallback(async (id) => {
    try {
      await apiMarkRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // silent
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await apiMarkAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  }, []);

  // ── Polling fallback ─────────────────────────
  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(() => {
      refreshUnreadCount();
    }, POLL_INTERVAL_MS);
  }, [refreshUnreadCount]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ── SSE connection ────────────────────────────
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) return; // already connected

    const token = getStoredAuthToken();
    if (!token) return;

    // Exceeded max retries — fall back to polling only
    if (sseRetryCountRef.current >= SSE_MAX_RETRIES) {
      sseFailedRef.current = true;
      startPolling();
      return;
    }

    const apiBase = getOAuthBaseUrl();
    const url = `${apiBase}/api/notifications/stream?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('notification', (event) => {
      try {
        const notification = JSON.parse(event.data);
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((c) => c + 1);
      } catch {
        // malformed event
      }
    });

    es.addEventListener('heartbeat', () => {
      lastHeartbeatRef.current = Date.now();
    });

    // Reset retry count on successful open
    es.onopen = () => {
      sseRetryCountRef.current = 0;
      sseFailedRef.current = false;
      lastHeartbeatRef.current = Date.now();
      // SSE is working — stop polling if it was running
      stopPolling();
      // Start heartbeat checker to detect stale connections
      if (heartbeatCheckerRef.current) {
        clearInterval(heartbeatCheckerRef.current);
      }
      heartbeatCheckerRef.current = setInterval(() => {
        if (Date.now() - lastHeartbeatRef.current > SSE_HEARTBEAT_TIMEOUT_MS) {
          // No heartbeat for 90s — connection is stale, force reconnect
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          clearInterval(heartbeatCheckerRef.current);
          heartbeatCheckerRef.current = null;
          if (!document.hidden && getStoredAuthToken()) {
            connectSSE();
          }
        }
      }, 30000); // check every 30s
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      if (heartbeatCheckerRef.current) {
        clearInterval(heartbeatCheckerRef.current);
        heartbeatCheckerRef.current = null;
      }
      sseRetryCountRef.current += 1;

      if (sseRetryCountRef.current >= SSE_MAX_RETRIES) {
        // Max retries exceeded — fall back to polling
        sseFailedRef.current = true;
        startPolling();
        return;
      }

      // Exponential backoff reconnect
      const delay = SSE_RECONNECT_DELAY_MS * Math.pow(SSE_BACKOFF_MULTIPLIER, sseRetryCountRef.current - 1);
      sseReconnectTimerRef.current = setTimeout(() => {
        sseReconnectTimerRef.current = null;
        if (getStoredAuthToken()) {
          connectSSE();
        }
      }, delay);
    };
  }, [startPolling, stopPolling]);

  const disconnectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (sseReconnectTimerRef.current) {
      clearTimeout(sseReconnectTimerRef.current);
      sseReconnectTimerRef.current = null;
    }
    if (heartbeatCheckerRef.current) {
      clearInterval(heartbeatCheckerRef.current);
      heartbeatCheckerRef.current = null;
    }
  }, []);

  // ── Page Visibility handling ────────────────
  // Disconnect SSE when tab is hidden to free browser connections for images
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden: close SSE to free connection for images/API
        disconnectSSE();
      } else {
        // Tab visible: reconnect SSE if user is logged in
        if (user && !eventSourceRef.current && !sseFailedRef.current) {
          sseRetryCountRef.current = 0;
          connectSSE();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, connectSSE, disconnectSSE]);

  // ── Lifecycle ─────────────────────────────────
  useEffect(() => {
    if (!user) {
      disconnectSSE();
      stopPolling();
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Reset SSE state on login
    sseRetryCountRef.current = 0;
    sseFailedRef.current = false;

    // Initial data load
    refreshUnreadCount();
    loadNotifications(0);

    // Try SSE first; polling starts only if SSE fails (see connectSSE)
    // Only connect if tab is visible (don't waste connections on background tabs)
    if (!document.hidden) {
      connectSSE();
    }

    return () => {
      disconnectSSE();
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Listen for auth events (logout / unauthorized)
  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents((event) => {
      if (event?.type === AUTH_EVENT_TYPES.UNAUTHORIZED || (event?.type === AUTH_EVENT_TYPES.PROFILE_UPDATED && !event.payload)) {
        disconnectSSE();
        stopPolling();
        setNotifications([]);
        setUnreadCount(0);
      }
    });
    return unsubscribe;
  }, [disconnectSSE, stopPolling]);

  const value = {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadNotifications,
    loadMore,
    markAsRead,
    markAllRead,
    refreshUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}

export default NotificationContext;
